import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["canvas", "tooltip"]

  connect() {
    this.loadData()
  }

  async loadData() {
    try {
      const [pointsRes, pinsRes] = await Promise.all([
        fetch("/map-points.json"),
        fetch("/travel-pins.json")
      ])
      this.mapPoints = await pointsRes.json()
      this.pins = await pinsRes.json()
      this.render()
      window.addEventListener("resize", this.handleResize)
    } catch (e) {
      console.error("Failed to load map data:", e)
    }
  }

  disconnect() {
    window.removeEventListener("resize", this.handleResize)
  }

  handleResize = () => {
    if (this.resizeTimeout) cancelAnimationFrame(this.resizeTimeout)
    this.resizeTimeout = requestAnimationFrame(() => this.render())
  }

  render() {
    const canvas = this.canvasTarget
    const svgNS = "http://www.w3.org/2000/svg"
    canvas.innerHTML = ""

    const svg = document.createElementNS(svgNS, "svg")
    // Matches dotted-map's actual viewBox for height=60, diagonal grid
    svg.setAttribute("viewBox", "0 0 126 60")
    svg.setAttribute("width", "100%")
    svg.setAttribute("preserveAspectRatio", "xMidYMid meet")
    svg.style.display = "block"

    // Background dots (world grid)
    const bgGroup = document.createElementNS(svgNS, "g")
    this.mapPoints.forEach(point => {
      const circle = document.createElementNS(svgNS, "circle")
      circle.setAttribute("cx", point.x)
      circle.setAttribute("cy", point.y)
      circle.setAttribute("r", "0.22")
      circle.setAttribute("fill", "#E5E7EB")
      bgGroup.appendChild(circle)
    })
    svg.appendChild(bgGroup)

    // Pin dots — coordinates are precomputed using dotted-map's Web Mercator projection
    const pinsGroup = document.createElementNS(svgNS, "g")

    this.pins.forEach((pin, index) => {
      // Outer glow
      const glow = document.createElementNS(svgNS, "circle")
      glow.setAttribute("cx", pin.x)
      glow.setAttribute("cy", pin.y)
      glow.setAttribute("r", "0.65")
      glow.setAttribute("fill", "rgba(97, 165, 194, 0.25)")
      pinsGroup.appendChild(glow)

      // Pin dot
      const dot = document.createElementNS(svgNS, "circle")
      dot.setAttribute("cx", pin.x)
      dot.setAttribute("cy", pin.y)
      dot.setAttribute("r", "0.35")
      dot.setAttribute("fill", "#61A5C2")
      pinsGroup.appendChild(dot)

      // Invisible hit area for hover
      const hit = document.createElementNS(svgNS, "circle")
      hit.setAttribute("cx", pin.x)
      hit.setAttribute("cy", pin.y)
      hit.setAttribute("r", "1.2")
      hit.setAttribute("fill", "transparent")
      hit.setAttribute("data-action", "mouseenter->dotted-map#showTooltip mouseleave->dotted-map#hideTooltip touchstart->dotted-map#showTooltip")
      hit.setAttribute("data-pin-index", index)
      hit.style.cursor = "pointer"
      pinsGroup.appendChild(hit)
    })
    svg.appendChild(pinsGroup)

    canvas.appendChild(svg)
  }

  showTooltip(event) {
    const index = event.target.getAttribute("data-pin-index")
    if (index === null) return

    const pin = this.pins[parseInt(index)]
    if (!pin) return

    const tooltip = this.tooltipTarget
    tooltip.innerHTML = `
      <span class="font-semibold text-charcoal">${pin.name}</span>
      <span class="text-neutral-400">${pin.country} &middot; ${pin.year}</span>
    `
    tooltip.classList.remove("opacity-0", "invisible")
    tooltip.classList.add("opacity-100", "visible")

    // Position tooltip near the cursor
    const mapRect = this.canvasTarget.getBoundingClientRect()
    const clientX = event.clientX || (event.touches && event.touches[0].clientX)
    const clientY = event.clientY || (event.touches && event.touches[0].clientY)

    let left = clientX - mapRect.left + 12
    let top = clientY - mapRect.top - 40

    // Keep tooltip within bounds
    const tooltipWidth = 200
    if (left + tooltipWidth > mapRect.width) {
      left = clientX - mapRect.left - tooltipWidth - 12
    }
    if (top < 0) {
      top = clientY - mapRect.top + 16
    }

    tooltip.style.left = `${left}px`
    tooltip.style.top = `${top}px`
  }

  hideTooltip() {
    const tooltip = this.tooltipTarget
    tooltip.classList.add("opacity-0", "invisible")
    tooltip.classList.remove("opacity-100", "visible")
  }
}
